import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { IntroCallout } from '../../components/ui/IntroCallout';
import { AiOutput, type AiCitation } from '../../components/ui/AiOutput';
import { AiActivityCard } from '../../components/ui/AiActivityCard';
import { AccessibleChart } from '../../components/charts/AccessibleChart';
import { useWorkspace } from '../../hooks/useWorkspace';
import { formatAmount } from '../../lib/format';
import { api } from '../../lib/api';

interface AgentRun {
  id: string;
  agent: string;
  model: string;
  prompt: string;
  output: { text: string };
  citations?: AiCitation[];
  cost_cents: string;
  started_at: string;
  finished_at: string | null;
}

interface UsageBreakdown {
  window: { from: string; to: string };
  total: { costCents: number; runs: number; promptTokens: number; completionTokens: number };
  byModel: Array<{ model: string; costCents: number; runs: number }>;
  byAgent: Array<{ agent: string; costCents: number; runs: number }>;
  byUser: Array<{ userId: string | null; displayName: string; costCents: number; runs: number }>;
  byRole: Array<{ role: string; costCents: number; runs: number }>;
  byDay: Array<{ day: string; costCents: number }>;
}

const AGENT_KEYS = [
  'finance_qa', 'anomaly', 'close_coach', 'dispute_composer', 'tax_credit_reconciler', 'cash_forecast',
  'cost_reduction', 'spend_analysis',
];

/**
 * Pure mapping from a grouped breakdown row list into AccessibleChart
 * `{label,value}` data. `value` is the cost in cents (Number-parsed already
 * by the API). Kept pure + exported so it can be unit-tested without React.
 */
export function toChartData<T>(
  rows: T[],
  label: (row: T) => string,
  value: (row: T) => number,
): Array<{ label: string; value: number }> {
  return rows.map((r) => ({ label: label(r), value: value(r) }));
}

export function AgentsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [agent, setAgent] = useState<string>('finance_qa');
  const [prompt, setPrompt] = useState('');
  const [runError, setRunError] = useState<string | null>(null);

  const { data: ws } = useWorkspace(wsId);
  const currency = ws?.base_currency ?? 'JPY';
  const fmt = (cents: number) => formatAmount(cents, currency, i18n.language);

  const runs = useQuery<AgentRun[]>({
    queryKey: ['agent-runs', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/agents/runs`)).data,
  });
  const usage = useQuery<UsageBreakdown>({
    queryKey: ['agent-usage', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/agents/usage`)).data,
  });
  const agentLabel = (a: string) => t(`agents.kinds.${a}`, a);
  const run = useMutation({
    mutationFn: async () =>
      (await api.post(`/workspaces/${wsId}/agents/run`, {
        agent,
        userPrompt: prompt,
      })).data,
    onSuccess: () => {
      setPrompt('');
      setRunError(null);
      qc.invalidateQueries({ queryKey: ['agent-runs', wsId] });
    },
    onError: (err: any) => setRunError(err?.response?.data?.message ?? err?.message ?? 'Agent run failed'),
  });

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-5xl">
        <h1 className="display text-2xl font-semibold mb-2 flex items-center gap-2">
          <Sparkles size={24} className="text-accent-600" />
          {t('agents.title')}
        </h1>
        <p className="text-sm text-ink-500 mb-6">{t('agents.subtitle')}</p>

        <IntroCallout storageKey="agents" title={t('intro.agents.title')} body={t('intro.agents.body')} />

        <Card className="p-5 mb-6">
          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-ink-500">{t('agents.agent')}</label>
            <select
              value={agent}
              onChange={(e) => setAgent(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
            >
              {AGENT_KEYS.map((a) => (
                <option key={a} value={a}>{t(`agents.kinds.${a}`)}</option>
              ))}
            </select>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            placeholder={t('agents.promptPlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm"
          />
          <button
            onClick={() => run.mutate()}
            disabled={run.isPending || !prompt.trim()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg font-medium h-10 px-5 text-sm bg-accent-600 text-white hover:bg-accent-700 disabled:opacity-50"
          >
            <Sparkles size={14} /> {t('common.run')}
          </button>
          {runError && (
            <div className="mt-3 text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              {runError}
            </div>
          )}
        </Card>

        <section id="ai-usage" className="mb-8 scroll-mt-20">
          <h2 className="font-medium mb-1">{t('agents.usage.title')}</h2>
          <p className="text-sm text-ink-500 mb-3">{t('agents.usage.subtitle')}</p>
          {usage.isLoading ? (
            <Card className="p-10 text-center text-ink-500">{t('common.loading')}</Card>
          ) : !usage.data ? null : usage.data.total.runs === 0 ? (
            <Card className="p-10 text-center text-ink-500 text-sm">{t('agents.usage.empty')}</Card>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Card className="p-4">
                  <div className="text-xs uppercase tracking-wider text-ink-500">{t('agents.usage.total')}</div>
                  <div className="num text-xl font-semibold mt-1">{fmt(usage.data.total.costCents)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs uppercase tracking-wider text-ink-500">{t('agents.usage.runs')}</div>
                  <div className="num text-xl font-semibold mt-1">{usage.data.total.runs.toLocaleString(i18n.language)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs uppercase tracking-wider text-ink-500">{t('agents.usage.promptTokens')}</div>
                  <div className="num text-xl font-semibold mt-1">{usage.data.total.promptTokens.toLocaleString(i18n.language)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs uppercase tracking-wider text-ink-500">{t('agents.usage.completionTokens')}</div>
                  <div className="num text-xl font-semibold mt-1">{usage.data.total.completionTokens.toLocaleString(i18n.language)}</div>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-4">
                  <AccessibleChart
                    type="pie"
                    title={t('agents.usage.byModel')}
                    summary={t('agents.usage.summary.byModel', { count: usage.data.byModel.length })}
                    valueFormatter={fmt}
                    series={[{ name: t('agents.usage.byModel'), data: toChartData(usage.data.byModel, (r) => r.model, (r) => r.costCents) }]}
                  />
                </Card>
                <Card className="p-4">
                  <AccessibleChart
                    type="bar"
                    title={t('agents.usage.byAgent')}
                    summary={t('agents.usage.summary.byAgent', { count: usage.data.byAgent.length })}
                    valueFormatter={fmt}
                    series={[{ name: t('agents.usage.byAgent'), data: toChartData(usage.data.byAgent, (r) => agentLabel(r.agent), (r) => r.costCents) }]}
                  />
                </Card>
                <Card className="p-4">
                  <AccessibleChart
                    type="bar"
                    title={t('agents.usage.byUser')}
                    summary={t('agents.usage.summary.byUser', { count: usage.data.byUser.length })}
                    valueFormatter={fmt}
                    series={[{ name: t('agents.usage.byUser'), data: toChartData(usage.data.byUser, (r) => r.displayName, (r) => r.costCents) }]}
                  />
                </Card>
                <Card className="p-4">
                  <div className="text-xs text-ink-500 mb-2">{t('agents.usage.roleTeamNote')}</div>
                  <AccessibleChart
                    type="bar"
                    title={t('agents.usage.byRole')}
                    summary={t('agents.usage.summary.byRole', { count: usage.data.byRole.length })}
                    valueFormatter={fmt}
                    height={220}
                    series={[{ name: t('agents.usage.byRole'), data: toChartData(usage.data.byRole, (r) => t(`agents.usage.roles.${r.role}`, r.role), (r) => r.costCents) }]}
                  />
                </Card>
                <Card className="p-4 lg:col-span-2">
                  <AccessibleChart
                    type="line"
                    title={t('agents.usage.daily')}
                    summary={t('agents.usage.summary.daily', { count: usage.data.byDay.length })}
                    valueFormatter={fmt}
                    series={[{ name: t('agents.usage.daily'), data: toChartData(usage.data.byDay, (r) => r.day, (r) => r.costCents) }]}
                  />
                </Card>
              </div>
            </>
          )}
        </section>

        <AiActivityCard wsId={wsId} className="mb-8" />

        <div>
          <h2 className="font-medium mb-3">{t('agents.recentRuns')}</h2>
          {runs.isLoading ? (
            <Card className="p-10 text-center text-ink-500">{t('common.loading')}</Card>
          ) : runs.data?.length === 0 ? (
            <Card className="p-10 text-center text-ink-500 text-sm">{t('agents.noRuns')}</Card>
          ) : (
            <ul className="space-y-3">
              {(runs.data ?? []).map((r) => (
                <li key={r.id}>
                  <Card className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-ink-500 flex items-center gap-2">
                        <span className="font-medium text-ink-800 dark:text-ink-100">
                          {t(`agents.kinds.${r.agent}`, r.agent)}
                        </span>
                      </div>
                      <span className="num text-xs text-ink-500">
                        ${(Number(r.cost_cents) / 100).toFixed(4)}
                      </span>
                    </div>
                    <div className="text-sm text-ink-700 dark:text-ink-200 whitespace-pre-wrap mb-2">
                      {r.prompt}
                    </div>
                    {r.output?.text && (
                      <div className="bg-ink-50 dark:bg-ink-900 rounded-lg p-3">
                        <AiOutput citations={r.citations}>{r.output.text}</AiOutput>
                      </div>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
